/**
 * What a post-hoc task is doing, read off the run's own events.
 *
 * A deck build emits `task.start`, then every `deck_method`,
 * `deck_storyline`, `deck_layout` and `build_deck` call, then `task.end` —
 * into `events.jsonl`, the same file the Activity tab reads. So the frame that
 * shows the build already has a source for *what it is doing* and needs no
 * second channel: `core/deckbuild.py` put the runtime's `event_sink` on the
 * run, and this is the other half of that.
 *
 * Here rather than in the component because deciding which events belong to a
 * build is an opinion — the run's own phases are in the same file, and showing
 * those under a "building a deck" heading would be a lie the component could
 * not be blamed for.
 */

import type { RunEvent } from "./types";

/**
 * What the ticker shows.
 *
 * Calls and the agent's own sentences, which between them are what "what is it
 * doing" means. Not every `tool.result`: the answer to a call is a row the
 * reader did not ask for, and two rows per call in a window eight lines tall
 * is one call visible instead of four. A *failed* result is the exception,
 * because a build that is going wrong is the one time this frame is worth
 * watching.
 */
const SHOWN = new Set([
  "task.start",
  // The summary's own rows: each document read, then the writing (#129).
  "task.step",
  "tool.use",
  "assistant.text",
  "runtime.error",
]);

function shown(event: RunEvent): boolean {
  if (event.type === "tool.result") return Boolean(event.is_error);
  return SHOWN.has(event.type);
}

/**
 * The events of the task that is running now, oldest first.
 *
 * Everything after the **last** `task.start`, because a run may have had a
 * deck built from it before: an earlier build's rows are history and belong in
 * the Activity tab, not under a spinner that claims to be live.
 *
 * Empty until the first event of the build reaches the page, which is the
 * honest state — the poll is on a 2.5-second tick and a build takes minutes.
 */
export function taskLog(events: RunEvent[], kind = "deck", since?: number): RunEvent[] {
  let start = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!;
    if (event.type === "task.start" && (event.kind ?? kind) === kind) {
      start = i;
      break;
    }
  }
  if (start < 0) return [];
  // `since` is when the task being watched started, in epoch seconds. Until
  // its own `task.start` reaches the page, the last one in the log is the
  // previous task's — and a finetune showed the last request's rows under the
  // new one for the first poll. The stamp is whole seconds, hence the slack.
  const began = Date.parse(String(events[start]!.at ?? ""));
  if (since !== undefined && !Number.isNaN(began) && began / 1000 < since - 2) return [];
  // `task.end` closes it: anything after belongs to the run, not the build.
  const rest = events.slice(start + 1);
  const end = rest.findIndex((e) => e.type === "task.end");
  return (end >= 0 ? rest.slice(0, end) : rest).filter(shown);
}
